import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAIMatchesByJobSeeker,
  getAllCompanies,
  getApplicationsByJobSeeker,
  getUserProfile,
  getApprovedJobPosts
} from '../services/api';

// Fake job opportunities data
const jobOpportunities = [
  {
    id: 1,
    companyName: 'TechCorp Solutions',
    position: 'Senior React Developer',
    vacancies: 3,
    requiredSkills: ['React', 'JavaScript', 'Node.js', 'TypeScript'],
    location: 'Remote',
    salary: '$80k - $120k'
  },
  {
    id: 2,
    companyName: 'Data Analytics Inc',
    position: 'Python Data Scientist',
    vacancies: 2,
    requiredSkills: ['Python', 'Machine Learning', 'SQL', 'TensorFlow'],
    location: 'New York',
    salary: '$90k - $140k'
  },
  {
    id: 3,
    companyName: 'Cloud Systems Ltd',
    position: 'DevOps Engineer',
    vacancies: 5,
    requiredSkills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Linux'],
    location: 'San Francisco',
    salary: '$100k - $150k'
  },
  {
    id: 4,
    companyName: 'Mobile Innovations',
    position: 'Flutter Developer',
    vacancies: 4,
    requiredSkills: ['Flutter', 'Dart', 'Mobile Development', 'Firebase'],
    location: 'Austin',
    salary: '$70k - $110k'
  }
];

const JobSeekerDashboard = () => {
  const { currentUser } = useAuth();
  const [matches, setMatches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiMatches, setAiMatches] = useState([]);
  const [approvedJobs, setApprovedJobs] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Load user profile
      const profileResult = await getUserProfile(currentUser.uid);
      if (profileResult.success) {
        setProfile(profileResult.profile);
      }

      // Load AI matches
      const matchesResult = await getAIMatchesByJobSeeker(currentUser.uid);
      if (matchesResult.success) {
        setMatches(matchesResult.matches);
      }

      // Load companies
      const companiesResult = await getAllCompanies();
      if (companiesResult.success) {
        setCompanies(companiesResult.companies.slice(0, 6));
      }

      // Load applications
      const applicationsResult = await getApplicationsByJobSeeker(currentUser.uid);
      if (applicationsResult.success) {
        setApplications(applicationsResult.applications);
      }

      // Load approved job posts from admin
      const approvedJobsResult = await getApprovedJobPosts();
      if (approvedJobsResult.success) {
        setApprovedJobs(approvedJobsResult.jobPosts);
      }

      // Calculate AI matches based on skills from BOTH fake jobs and approved jobs
      if (profileResult.success && profileResult.profile && profileResult.profile.skills) {
        const userSkills = profileResult.profile.skills.map(skill => skill.toLowerCase());

        // Combine fake jobs and approved jobs for matching
        const allJobs = [
          ...jobOpportunities,
          ...(approvedJobsResult.success ? approvedJobsResult.jobPosts : [])
        ];

        const matchedJobs = allJobs.filter(job => {
          // Check if any user skill matches any required skill
          return job.requiredSkills && job.requiredSkills.some(requiredSkill =>
            userSkills.some(userSkill =>
              requiredSkill.toLowerCase().includes(userSkill) ||
              userSkill.includes(requiredSkill.toLowerCase())
            )
          );
        }).map(job => {
          // Calculate match percentage
          const matchedSkills = job.requiredSkills.filter(requiredSkill =>
            userSkills.some(userSkill =>
              requiredSkill.toLowerCase().includes(userSkill) ||
              userSkill.includes(requiredSkill.toLowerCase())
            )
          );
          const matchPercentage = Math.round((matchedSkills.length / job.requiredSkills.length) * 100);

          return {
            ...job,
            matchedSkills,
            matchPercentage
          };
        }).sort((a, b) => b.matchPercentage - a.matchPercentage);

        setAiMatches(matchedJobs);
      }

      setLoading(false);
    };

    loadData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">💼</div>
          <div className="text-2xl font-bold text-gray-700">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8">
      <div className="container mx-auto px-4">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <div className="flex items-center mb-3">
                <span className="text-5xl mr-4">👋</span>
                <h1 className="text-4xl font-bold">
                  Welcome back, {currentUser.displayName || 'Job Seeker'}!
                </h1>
              </div>
              <p className="text-blue-100 text-lg ml-16">
                Here's what's happening with your job search today
              </p>
            </div>
            <div className="hidden md:block text-8xl opacity-20">
              💼
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">AI Matches</p>
                <p className="text-4xl font-bold">{matches.length}</p>
                <p className="text-blue-200 text-xs mt-1">Active opportunities</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">🎯</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-1">Applications</p>
                <p className="text-4xl font-bold">{applications.length}</p>
                <p className="text-green-200 text-xs mt-1">Submitted</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">📝</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium mb-1">Profile Views</p>
                <p className="text-4xl font-bold">142</p>
                <p className="text-purple-200 text-xs mt-1">This month</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">👁️</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium mb-1">Profile Score</p>
                <p className="text-4xl font-bold">
                  {profile ? '85%' : '0%'}
                </p>
                <p className="text-yellow-200 text-xs mt-1">Completion rate</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">⭐</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Matched Job Opportunities */}
            {aiMatches.length > 0 && (
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-3 mr-4">
                      <span className="text-3xl">🎯</span>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        AI Matched Opportunities
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Jobs perfectly matching your skills
                      </p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg">
                    {aiMatches.length} Match{aiMatches.length > 1 ? 'es' : ''}
                  </div>
                </div>
                <div className="space-y-5">
                  {aiMatches.map((job) => (
                    <div
                      key={job.id}
                      className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 hover:shadow-2xl hover:border-green-500 transition-all duration-300 transform hover:scale-[1.02]"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">
                            {job.position}
                          </h3>
                          <p className="text-gray-700 font-semibold">🏢 {job.companyName}</p>
                        </div>
                        <div className="text-right">
                          <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {job.matchPercentage}% Match
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                        <div className="flex items-center text-gray-700">
                          <span className="mr-2">📍</span>
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <span className="mr-2">💰</span>
                          <span>{job.salary}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <span className="mr-2">👥</span>
                          <span>{job.vacancies} {job.vacancies === 1 ? 'vacancy' : 'vacancies'}</span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Required Skills:</p>
                        <div className="flex flex-wrap gap-1">
                          {job.requiredSkills.map((skill, index) => {
                            const isMatched = job.matchedSkills.includes(skill);
                            return (
                              <span
                                key={index}
                                className={`text-xs px-2 py-1 rounded ${
                                  isMatched
                                    ? 'bg-green-600 text-white font-semibold'
                                    : 'bg-gray-200 text-gray-700'
                                }`}
                              >
                                {skill}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <a
                          href={job.companyLink || "https://binarytechstation.com"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
                        >
                          ✓ Apply Now
                        </a>
                        <Link
                          to={`/job/${job.id}`}
                          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
                        >
                          📋 View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Job Opportunities */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-3 mr-4">
                    <span className="text-3xl">💼</span>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      Job Opportunities
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {jobOpportunities.length + approvedJobs.length} positions available
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-5">
                {/* Always show fake job opportunities first */}
                {jobOpportunities.map((job) => (
                  <div
                    key={job.id}
                    className="border-2 border-gray-200 bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">
                          {job.position}
                        </h3>
                        <p className="text-gray-700 font-semibold">🏢 {job.companyName}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">📍</span>
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">💰</span>
                        <span>{job.salary}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">👥</span>
                        <span>{job.vacancies} {job.vacancies === 1 ? 'vacancy' : 'vacancies'}</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Required Skills:</p>
                      <div className="flex flex-wrap gap-1">
                        {job.requiredSkills.map((skill, index) => (
                          <span
                            key={index}
                            className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <a
                        href={job.companyLink || "https://binarytechstation.com"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
                      >
                        ✓ Apply Now
                      </a>
                      <Link
                        to={`/job/${job.id}`}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
                      >
                        📋 View Details
                      </Link>
                    </div>
                  </div>
                ))}

                {/* Then show approved jobs from admin */}
                {approvedJobs.map((job) => (
                  <div
                    key={job.id}
                    className="border border-gray-200 bg-gradient-to-r from-white to-blue-50 rounded-xl p-5 hover:border-blue-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">
                          {job.position}
                        </h3>
                        <p className="text-gray-700 font-semibold">🏢 {job.customCompanyName || job.companyName}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">📍</span>
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">💰</span>
                        <span>{job.salary}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">👥</span>
                        <span>{job.vacancies} {job.vacancies === 1 ? 'vacancy' : 'vacancies'}</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Required Skills:</p>
                      <div className="flex flex-wrap gap-1">
                        {job.requiredSkills && job.requiredSkills.map((skill, index) => (
                          <span
                            key={index}
                            className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <a
                        href={job.companyLink || "https://binarytechstation.com"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
                      >
                        ✓ Apply Now
                      </a>
                      <Link
                        to={`/job/${job.id}`}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
                      >
                        📋 View Details
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Completion */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-3 mr-3">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Profile Completion
                </h3>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 font-medium">Progress</span>
                  <span className="font-bold text-blue-600">
                    {profile ? '85%' : '20%'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-4 rounded-full shadow-lg transition-all"
                    style={{ width: profile ? '85%' : '20%' }}
                  ></div>
                </div>
              </div>
              {!profile || !profile.skills ? (
                <Link
                  to="/profile/jobseeker"
                  className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                >
                  ✨ Complete Profile
                </Link>
              ) : (
                <Link
                  to="/profile/jobseeker"
                  className="block w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white text-center py-3 rounded-xl hover:from-gray-700 hover:to-gray-800 font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                >
                  ✏️ Edit Profile
                </Link>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-3 mr-3">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Quick Actions
                </h3>
              </div>
              <div className="space-y-3">
                <Link
                  to="/profile/jobseeker"
                  className="block w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-center py-3 rounded-xl hover:from-blue-600 hover:to-indigo-600 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  👤 Update Profile
                </Link>
                <Link
                  to="/jobs"
                  className="block w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center py-3 rounded-xl hover:from-green-600 hover:to-emerald-600 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  🔍 Browse Jobs
                </Link>
                <Link
                  to="/settings"
                  className="block w-full bg-gradient-to-r from-gray-500 to-gray-600 text-white text-center py-3 rounded-xl hover:from-gray-600 hover:to-gray-700 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  ⚙️ Settings
                </Link>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-3 mr-3">
                  <span className="text-2xl">📅</span>
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Recent Activity
                </h3>
              </div>
              <div className="space-y-3">
                {applications.length > 0 ? (
                  applications.slice(0, 3).map((app, index) => (
                    <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-xl border-l-4 border-purple-500">
                      <p className="text-gray-800 font-bold text-sm">✓ Application submitted</p>
                      <p className="text-gray-600 text-xs mt-1">{app.appliedDate}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <p className="text-gray-400 text-sm italic">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerDashboard;
